import React, { useMemo } from 'react';

import { cs } from '@/utils/className';

import styles from './index.module.less';
import { type IndicatorTone, resolveIndicatorAppearance } from './resolveIndicatorAppearance';

export type { IndicatorTone, IndicatorAppearance } from './resolveIndicatorAppearance';
export { resolveIndicatorAppearance } from './resolveIndicatorAppearance';

export interface IndicatorProps {
  /**
   * 语义色阶（优先）。推荐业务侧先映射好再传入：
   * success | danger | warning | info | default
   */
  tone?: IndicatorTone;
  /**
   * 业务状态名或语义 tone；未传 tone 时由此解析外观。
   * 兼容历史字段，新代码优先用 tone。
   */
  status?: string;
  /** @deprecated 同 status，兼容旧调用 */
  type?: string;
  /**
   * 是否闪烁。仅布尔值覆盖解析结果：true 强制闪、false 强制不闪。
   * 旧调用把状态字符串传给 flicker 时忽略该值，避免运行中等终态被误闪。
   */
  flicker?: boolean | string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

/** 是否属于应自动闪烁的进行中状态 */
export function isProgressIndicatorStatus(status?: string) {
  return resolveIndicatorAppearance(status).flicker;
}

/**
 * 状态指示器：样式只认语义 tone，业务状态在 TS 中解析后传入。
 */
export function Indicator({ tone, status, type, flicker, className, style, children }: IndicatorProps) {
  const appearance = useMemo(() => {
    if (tone) {
      return { tone, flicker: false };
    }
    return resolveIndicatorAppearance(status || type || 'default');
  }, [tone, status, type]);

  // 旧 class 组件把状态名当作 flicker 传入，样式表再决定哪些状态真正闪烁
  const shouldFlicker = typeof flicker === 'boolean' ? flicker : appearance.flicker;

  return (
    <span className={cs(styles.wrap, className)} style={style}>
      <i className={cs(styles.icon, styles[appearance.tone], shouldFlicker && styles.flicker)} />
      {children != null && children !== false && <span className={styles.content}>{children}</span>}
    </span>
  );
}
